/* eslint-disable no-underscore-dangle */

import dayjs from 'dayjs';
import {
  TAXON_TYPE,
  TAXON_TYPE_DISPLAY_NAME_FOR_PERSON,
  TAXON_TYPE_DISPLAY_NAME_FOR_RELATED_PERSON,
} from '@/constants/purchase';
import { getCurrencyPriceByCoins } from '@/utils/price';
import { getDefaultContentMeta } from '@/utils/linkUtils';

function getSubscriptionStatus(cancelDate) {
  if (!cancelDate) {
    return {
      name: 'Active',
      code: 'active',
    };
  }
  if (cancelDate > Date.now()) {
    return {
      name: `Will be cancelled at ${dayjs.unix(cancelDate / 1000).format('D MMM')}`,
      code: 'cancelling',
      color: 'error',
    };
  }
  return {
    name: 'Cancelled',
    code: 'cancelled',
    color: 'error',
  };
}

function formatDate(date) {
  return dayjs.unix(date / 1000).format('D MMMM YYYY');
}

class PersonToPersonFormatter {
  /**
   *
   * @param {Object} item response item of PersonToPerson result
   * @param {('person' | 'relatedPerson')} personType
   */
  constructor(item, personType) {
    const { objectID, taxonType, taxon, _createdAt } = item;
    this.id = objectID;
    this.taxonType = taxonType;
    this.taxon = taxon;
    this.person = item.person;
    this.taxonTypeDisplayName = personType === 'person'
      ? TAXON_TYPE_DISPLAY_NAME_FOR_PERSON
      : TAXON_TYPE_DISPLAY_NAME_FOR_RELATED_PERSON;
    this.createdDate = formatDate(_createdAt);

    this.isFree = this.taxon.amount === 0;
    this.purchaseDisplayName = this.taxonTypeDisplayName[this.taxonType];
    this.price = this.isFree ? 'FREE' : getCurrencyPriceByCoins(this.taxon.amount, this.taxon.currency);
  }

  _getUserAvatar() {
    const {
      avatarUrl,
      displayName,
      email,
      username,
    } = this.person;

    return {
      src: avatarUrl,
      name: displayName || email || username,
    };
  }

  _formatDigitalItem() {
    const {
      id,
      type,
      imageUrl,
      title,
    } = this.taxon;
    const { title: subTypeTitle, displayIcon } = getDefaultContentMeta(type);

    return {
      avatar: {
        src: imageUrl,
        fallbackSrc: displayIcon,
      },
      link: `/product/${id}`,
      purchaseDisplayName: subTypeTitle,
      purchaseText: title,
      purchaseSubText: this.person.displayName,
      status: {
        name: this.taxonType === TAXON_TYPE.COURSE ? 'Active' : 'Complete',
      },
    };
  }

  _formatTip() {
    const {
      username,
      displayName,
    } = this.person;
    const {
      description,
    } = this.taxon;

    return {
      avatar: this._getUserAvatar(),
      link: `/${username}`,
      purchaseText: displayName,
      comment: description,
      status: {
        name: 'Complete',
      },
    };
  }

  _formatSubscription() {
    const {
      username,
      displayName,
    } = this.person;

    const {
      startDate,
      cancelDate,
      endDate,
      interval,
    } = this.taxon;


    let price = this.price;
    if (!this.isFree) {
      price = `${this.price}/${interval}`;
    }

    return {
      avatar: this._getUserAvatar(),
      link: `/${username}`,
      purchaseText: displayName,
      status: getSubscriptionStatus(cancelDate || endDate),
      createdDate: formatDate(startDate),
      cancelDate,
      endDate,
      price,
    };
  }

  _formatEmailCapture() {
    const {
      username,
      displayName,
    } = this.person;

    return {
      avatar: this._getUserAvatar(),
      link: username ? `/${username}` : null,
      purchaseText: displayName || 'Guest',
      status: {
        name: 'Complete',
      },
    };
  }

  _formatTaxon() {
    let result = {};
    switch (this.taxonType) {
      case TAXON_TYPE.DIGITAL_ITEM:
        result = this._formatDigitalItem();
        break;
      case TAXON_TYPE.COURSE:
        result = this._formatDigitalItem();
        delete result.purchaseDisplayName;
        break;
      case TAXON_TYPE.TIP:
        result = this._formatTip();
        break;
      case TAXON_TYPE.SUBSCRIPTION:
        result = this._formatSubscription();
        break;
      case TAXON_TYPE.EMAIL_CAPTURE:
        result = this._formatEmailCapture();
        break;
      default:
        throw new Error(`Not defined taxon type '${this.taxonType}'`);
    }
    return result;
  }

  format() {
    const taxon = this._formatTaxon();

    return {
      id: this.id,
      purchaseDisplayName: this.purchaseDisplayName,
      createdDate: this.createdDate,
      price: this.price,
      person: this.person,
      taxonMeta: {
        id: this.taxon.id,
        type: this.taxonType,
      },
      ...taxon,
    };
  }
}

export default PersonToPersonFormatter;
